import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendar, 
  faComment, 
  faFileAlt, 
  faUser, 
  faCheckCircle,
  faPlus,
  faSpinner,
  faPhone,
  faEnvelope,
  faHandshake,
  faLightbulb,
  faRightLeft,
} from '@fortawesome/free-solid-svg-icons';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import AddActivityPage from '../../AddActivityPage/AddActivityPage';
import AssignLeadModal from '../../AssignLeadModal/AssignLeadModal';

interface CoreRelationshipCardProps {
  stakeholderData: any;
  type: 'individual' | 'organization';
  formatDate: (date: string | Date | null | undefined) => string;
  getInitials: (name: string) => string;
  stakeholderId: string;
}

interface Activity {
  activity_id: number;
  activity_type: string;
  activity_text: string;
  created_at: string;
  created_by: string;
}

// Activity type icon mapping
const getActivityIcon = (activityType: string) => {
  switch (activityType) {
    case 'meeting':
      return faHandshake;
    case 'call':
      return faPhone;
    case 'email':
      return faEnvelope;
    case 'note':
      return faComment;
    case 'document':
      return faFileAlt;
    case 'task':
      return faCheckCircle;
    default:
      return faComment;
  }
};

// Activity type color mapping
const getActivityColor = (activityType: string): string => {
  switch (activityType) {
    case 'meeting':
      return '#0ea5e9';
    case 'call':
      return '#10b981';
    case 'email':
      return '#8b5cf6';
    case 'note':
      return '#f59e0b';
    case 'document':
      return '#ef4444';
    case 'task':
      return '#06b6d4';
    default:
      return '#64748b';
  }
};

const CoreRelationshipCard: React.FC<CoreRelationshipCardProps> = ({
  stakeholderData,
  type,
  formatDate,
  getInitials,
  stakeholderId,
}) => {
  const [isAddActivityModalOpen, setIsAddActivityModalOpen] = useState(false);
  const [isAssignLeadModalOpen, setIsAssignLeadModalOpen] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignedLeadName, setAssignedLeadName] = useState<string>('');
  const [assignedLeadId, setAssignedLeadId] = useState<string | null>(null);
  const [employeeNames, setEmployeeNames] = useState<Map<string, string>>(new Map());
  const [isAgent, setIsAgent] = useState(false);

  // Extract actual ID (handle org- prefix)
  const actualClientId = useMemo(() => {
    if (stakeholderId.startsWith('org-')) {
      return stakeholderId.replace('org-', '');
    }
    return stakeholderId;
  }, [stakeholderId]);

  const parseActivitiesFromData = useCallback((data: any, fromAgent: boolean): Activity[] => {
    let parsedActivities: Activity[] = [];
    if (fromAgent) {
      const notesField = (data as any).Notes ?? (data as any).notes;
      if (notesField) {
        try {
          const parsed = typeof notesField === 'string' ? JSON.parse(notesField) : notesField;
          if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && Array.isArray(parsed.recent_activity)) {
            parsedActivities = parsed.recent_activity;
          }
        } catch (e) {
          console.error('Error parsing agent notes for activities:', e);
        }
      }
    } else {
      const activitiesField = (data as any).recent_activity ?? (data as any).RecentActivity;
      if (activitiesField) {
        try {
          const activitiesData = typeof activitiesField === 'string' ? JSON.parse(activitiesField) : activitiesField;
          if (Array.isArray(activitiesData)) {
            parsedActivities = activitiesData;
          } else if (typeof activitiesData === 'object' && activitiesData !== null) {
            parsedActivities = [activitiesData];
          }
        } catch (error) {
          console.error('Error parsing activities:', error);
        }
      }
    }
    return parsedActivities.map((activity: any, idx) => ({
      activity_id: activity.activity_id || idx + 1,
      activity_type: activity.activity_type || 'note',
      activity_text: activity.activity_text || '',
      created_at: activity.created_at || '',
      created_by: activity.created_by || '',
    }));
  }, []);

  // Fetch activities and assigned lead (client or agent)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        let clientData: any = null;
        let agentData: any = null;
        try {
          clientData = await ClientService.getClientById(actualClientId);
        } catch {
          clientData = null;
        }
        if (!clientData) {
          try {
            agentData = await AgentsService.getAgent(actualClientId);
          } catch {
            agentData = null;
          }
        }

        if (clientData) {
          setIsAgent(false);
          const parsed = parseActivitiesFromData(clientData, false);
          setActivities(parsed);

          const employees = await EmployeeService.getEmployees();
          const namesMap = new Map<string, string>();
          employees.forEach((emp: any) => {
            namesMap.set(emp.EmployeeID, `${emp.FirstName || ''} ${emp.LastName || ''}`.trim());
          });
          setEmployeeNames(namesMap);

          const leadId = (clientData as any).assigned_lead ?? (clientData as any).AssignedLead;
          setAssignedLeadId(leadId || null);
          if (leadId && namesMap.has(leadId)) {
            setAssignedLeadName(namesMap.get(leadId) || '');
          } else {
            setAssignedLeadName('');
          }
        } else if (agentData) {
          setIsAgent(true);
          const parsed = parseActivitiesFromData(agentData, true);
          setActivities(parsed);

          const employees = await EmployeeService.getEmployees();
          const namesMap = new Map<string, string>();
          employees.forEach((emp: any) => {
            namesMap.set(emp.EmployeeID, `${emp.FirstName || ''} ${emp.LastName || ''}`.trim());
          });
          setEmployeeNames(namesMap);

          setAssignedLeadId(null);
          setAssignedLeadName('');
        } else {
          setActivities([]);
        }
      } catch (error) {
        console.error('Error fetching activity data:', error);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [actualClientId, stakeholderData, parseActivitiesFromData]);

  const handleAddActivity = () => {
    setIsAddActivityModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddActivityModalOpen(false);
  };

  const handleActivityAdded = async () => {
    try {
      if (isAgent) {
        const agentData = await AgentsService.getAgent(actualClientId);
        if (!agentData) {
          setActivities([]);
          return;
        }
        setActivities(parseActivitiesFromData(agentData, true));
      } else {
        const clientData = await ClientService.getClientById(actualClientId);
        if (!clientData) {
          setActivities([]);
          return;
        }
        setActivities(parseActivitiesFromData(clientData, false));
      }
    } catch (error) {
      console.error('Error refreshing activities:', error);
    }
  };

  const handleAssignLead = () => {
    setIsAssignLeadModalOpen(true);
  };

  const handleCloseAssignLeadModal = () => {
    setIsAssignLeadModalOpen(false);
  };

  const handleLeadAssigned = async () => {
    if (isAgent) return;
    try {
      const clientData = await ClientService.getClientById(actualClientId);
      if (!clientData) {
        setAssignedLeadName('');
        setAssignedLeadId(null);
        return;
      }

      const employees = await EmployeeService.getEmployees();
      const namesMap = new Map<string, string>();
      employees.forEach((emp: any) => {
        namesMap.set(emp.EmployeeID, `${emp.FirstName || ''} ${emp.LastName || ''}`.trim());
      });

      const leadId = (clientData as any).assigned_lead ?? (clientData as any).AssignedLead;
      setAssignedLeadId(leadId || null);
      if (leadId && namesMap.has(leadId)) {
        setAssignedLeadName(namesMap.get(leadId) || '');
      } else {
        setAssignedLeadName('');
      }
    } catch (error) {
      console.error('Error refreshing assigned lead:', error);
    }
  };

  // Format relative time for activities
  const formatRelativeTime = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    } catch {
      return '';
    }
  };

  // Get display name for assigned lead
  const displayLeadName = assignedLeadName || stakeholderData.assignedLead || '';

  return (
    <>
      <div className="stakeholder-details-card stakeholder-details-card-full">
        <div className="stakeholder-card-header">
          <div className="stakeholder-card-header-left">
            <FontAwesomeIcon icon={faUser} className="stakeholder-card-icon" />
            <h3 className="stakeholder-card-title">Core Relationship Details</h3>
          </div>
        </div>
        <div className="stakeholder-card-content">
          <div className="stakeholder-detail-row">
            <div className="stakeholder-detail-label">Assigned SEPT Lead</div>
            <div className="stakeholder-lead-chip">
              <div className="stakeholder-lead-info">
                <div className="stakeholder-lead-avatar">
                  {getInitials(displayLeadName || 'N/A')}
                </div>
                <div className="stakeholder-lead-details">
                  <div className="stakeholder-lead-name">{displayLeadName || 'Not Assigned'}</div>
                  {displayLeadName && (
                    <div className="stakeholder-lead-title">Relationship Manager</div>
                  )}
                </div>
              </div>
              {!isAgent && (
                <button
                  className="stakeholder-edit-button stakeholder-change-lead-button"
                  aria-label="Change assigned lead"
                  title="Change assigned lead"
                  onClick={handleAssignLead}
                >
                  <FontAwesomeIcon icon={faRightLeft} />
                  <span className="stakeholder-change-lead-label">Change</span>
                </button>
              )}
            </div>
          </div>
          <div className="stakeholder-detail-row stakeholder-engagement-row">
            <div className="stakeholder-detail-label">Last Engagement Date</div>
            <div className="stakeholder-detail-value">
              <div className="stakeholder-engagement-info">
                <FontAwesomeIcon icon={faCalendar} />
                <span>{formatDate(
                  // A7: derive Last Engagement Date from the newest activity (created_at, unshift'd to
                  // index 0), then fall back to the creation date (reading the PascalCase field the API
                  // actually returns) — so it stops reading "N/A" once an activity is logged.
                  (activities.length > 0 ? activities[0].created_at : undefined)
                  || stakeholderData.lastActive
                  || stakeholderData.contact_created_date
                  || stakeholderData.ContactCreatedDate
                )}</span>
              </div>
              {activities.length > 0 && (
                <div className="stakeholder-engagement-note">
                  {activities[0].activity_type.charAt(0).toUpperCase() + activities[0].activity_type.slice(1)}
                </div>
              )}
            </div>
          </div>
          <div className="stakeholder-detail-row">
            <div className="stakeholder-detail-label">Add Activity</div>
            <div className="stakeholder-detail-value">
              <button 
                className="stakeholder-add-activity-btn"
                onClick={handleAddActivity}
              >
                <FontAwesomeIcon icon={faPlus} />
                Log Activity
              </button>
            </div>
          </div>
          <div className="stakeholder-recent-activity">
            <div className="stakeholder-activity-header">Recent Activity</div>
            {loading ? (
              <div className="stakeholder-activity-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading activities...</span>
              </div>
            ) : activities.length === 0 ? (
              <div className="stakeholder-activity-empty">
                <div className="stakeholder-activity-empty-icon">
                  <FontAwesomeIcon icon={faLightbulb} />
                </div>
                <p>No activities logged yet</p>
                <button 
                  className="stakeholder-activity-empty-btn"
                  onClick={handleAddActivity}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Log your first activity
                </button>
              </div>
            ) : (
              <div className="stakeholder-activity-list">
                {activities.slice(0, 5).map((activity) => {
                  const createdByName = employeeNames.get(activity.created_by) || 'Unknown';
                  const activityIcon = getActivityIcon(activity.activity_type);
                  const activityColor = getActivityColor(activity.activity_type);
                  
                  return (
                    <div key={activity.activity_id} className="stakeholder-activity-item">
                      <FontAwesomeIcon 
                        icon={activityIcon} 
                        className="stakeholder-activity-icon"
                        style={{ color: activityColor }}
                      />
                      <div className="stakeholder-activity-content">
                        <div className="stakeholder-activity-text">
                          <span className="stakeholder-activity-author">{createdByName}</span>
                          {' '}{activity.activity_text}
                        </div>
                        <div className="stakeholder-activity-time">
                          {formatRelativeTime(activity.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {activities.length > 5 && (
                  <div className="stakeholder-activity-more">
                    +{activities.length - 5} more activities
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isAddActivityModalOpen && (
        <AddActivityPage
          onClose={handleCloseModal}
          stakeholderId={actualClientId}
          type={type}
          stakeholderData={stakeholderData}
          onActivityAdded={handleActivityAdded}
        />
      )}

      {isAssignLeadModalOpen && !isAgent && (
        <AssignLeadModal
          onClose={handleCloseAssignLeadModal}
          stakeholderId={actualClientId}
          type={type}
          stakeholderData={stakeholderData}
          onLeadAssigned={handleLeadAssigned}
          currentAssignedLeadId={assignedLeadId}
        />
      )}
    </>
  );
};

export default CoreRelationshipCard;
